import type { ContextLegend } from "../Components/ReportComponent/model/types";
import { mockLayers } from "../global/constants/layers";
import { CATEGORICAL_LEGEND_FIELDS } from "../global/constants/categoricalLegends";
import { palette, hashIndex } from "../global/layerStyles/apl_pe_observatorio";
import { geoServer } from "./geoServer";

export interface CategoricalClassLegendItem {
  label: string;
  color: string;
}

async function fetchCategoricalClassLegend(
  workspace: string,
  layerName: string,
  field: string
): Promise<CategoricalClassLegendItem[]> {
  try {
    // Sem bbox nem maxFeatures de propósito: a legenda precisa de todas as
    // classes da camada, não só das visíveis. `propertyName` deixa a geometria
    // fora da resposta, então o payload fica pequeno.
    const res = await geoServer.get("wfs", {
      params: {
        service: "WFS",
        version: "1.0.0",
        request: "GetFeature",
        typeName: `${workspace}:${layerName}`,
        outputFormat: "application/json",
        propertyName: field,
      },
    });
    const features: any[] = res.data?.features ?? [];
    const seen = new Set<string>();
    features.forEach((f) => {
      const value: string = f.properties?.[field];
      if (value) seen.add(value);
    });
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b, "pt-BR"))
      .map((label) => ({ label, color: palette[hashIndex(label)] }));
  } catch {
    return [];
  }
}

/**
 * Preenche as legendas das camadas coloridas por hash (`CATEGORICAL_LEGEND_FIELDS`)
 * com as classes reais e suas cores. Sem isso o relatório mostraria só um item
 * genérico com a cor de município sem vocação, já que `generateLegendData`
 * chama o estilo dessas camadas com propriedades vazias.
 */
export async function hydrateCategoricalLegends(
  legends: ContextLegend[]
): Promise<ContextLegend[]> {
  const targets = legends
    .map((legend) => mockLayers.find((l) => l.title === legend.layerName))
    .filter(
      (layer): layer is NonNullable<typeof layer> =>
        Boolean(layer) &&
        Boolean(layer!.workspace) &&
        layer!.layerName in CATEGORICAL_LEGEND_FIELDS
    );

  if (targets.length === 0) return legends;

  const byTitle = new Map<string, CategoricalClassLegendItem[]>();
  await Promise.all(
    targets.map(async (layer) => {
      const field = CATEGORICAL_LEGEND_FIELDS[layer.layerName];
      const items = await fetchCategoricalClassLegend(
        layer.workspace as string,
        layer.layerName,
        field
      );
      if (items.length > 0) byTitle.set(layer.title, items);
    })
  );

  return legends.map((legend) => {
    const items = byTitle.get(legend.layerName);
    if (!items) return legend;
    return {
      ...legend,
      items: items.map((item) => ({
        label: item.label,
        style: {
          type: "polygon" as const,
          color: item.color,
          fillColor: item.color,
          fillOpacity: 0.6,
          weight: 2,
        },
      })),
    };
  });
}
